// JobAllRequest.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { Color } from "../../../../GlobalStyles";

import JobCard from "./JobCard";
import SideNavigationBar from "../../../reusables/SideNavigationBar/SideNavigationBar";
import { useJobs } from "../../../../hooks/useJobs";

function JobAllRequest() {
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [jobTypeFilters, setJobTypeFilters] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const buildFilters = useCallback(() => {
    const filters = { filterType: "admin" };

    if (debouncedSearchQuery.trim()) {
      filters.search = debouncedSearchQuery.trim();
    }

    if (jobTypeFilters.length > 0) {
      filters.jobType = jobTypeFilters;
    }

    return filters;
  }, [debouncedSearchQuery, jobTypeFilters]);

  const { data, isLoading: initialLoading, fetchNextPage: fetchMore, hasNextPage: hasMore, isFetchingNextPage: loading, error, refetch } = useJobs({
    limit: 10,
    filters: buildFilters()
  });

  useEffect(() => {
    refetch();
  }, [debouncedSearchQuery, jobTypeFilters, refetch]);

  const allJobs = data?.pages.flatMap(page => page.data) || [];

  const observer = useRef();
  const lastJobElementRef = useCallback(
    (node) => {
      if (initialLoading || loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            fetchMore();
          }
        },
        { threshold: 0.5 }
      );

      if (node) observer.current.observe(node);
    },
    [initialLoading, loading, hasMore, fetchMore]
  );

  const handleJobTypeFilter = (jobType) => {
    setJobTypeFilters((prev) => {
      const isActive = prev.includes(jobType);
      return isActive ? prev.filter((item) => item !== jobType) : [...prev, jobType];
    });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  if (error) {
    return <div className="p-6">Error</div>;
  }

  return (
    <Box sx={{ ml: 10 }}>
      <SideNavigationBar />

      <Typography variant="h2" sx={{ fontSize: "40px", m: 5, mb: 3 }}>
        All Job Requests
      </Typography>

      <div className="flex items-center justify-between">
        <Box sx={{ mx: 5, mb: 3, width: "50%" }}>
          <TextField
            fullWidth
            placeholder="Search by company name or job title..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              borderRadius: "2rem",
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                borderRadius: "2rem",
              }
            }}
          />
        </Box>

        <Box sx={{ mx: 5, mb: 4, display: 'flex', gap: 2 }}>
          <Button
            variant={jobTypeFilters.includes("Full-Time") ? "contained" : "outlined"}
            onClick={() => handleJobTypeFilter("Full-Time")}
            sx={{
              bgcolor: jobTypeFilters.includes("Full-Time") ? Color.primary3 : "transparent",
              color: jobTypeFilters.includes("Full-Time") ? "#fff" : Color.primary3,
              borderColor: Color.primary3,
              borderRadius: "2rem",
              "&:hover": {
                bgcolor: jobTypeFilters.includes("Full-Time") ? Color.primary3 : "rgba(0, 0, 0, 0.04)",
                borderColor: Color.primary3,
              },
            }}
          >
            Full-Time
          </Button>
          <Button
            variant={jobTypeFilters.includes("Full Time Intern") ? "contained" : "outlined"}
            onClick={() => handleJobTypeFilter("Full Time Intern")}
            sx={{
              bgcolor: jobTypeFilters.includes("Full Time Intern") ? Color.primary3 : "transparent",
              color: jobTypeFilters.includes("Full Time Intern") ? "#fff" : Color.primary3,
              borderColor: Color.primary3,
              borderRadius: "2rem",
              "&:hover": {
                bgcolor: jobTypeFilters.includes("Full Time Intern") ? Color.primary3 : "rgba(0, 0, 0, 0.04)",
                borderColor: Color.primary3,
              },
            }}
          >
            Full Time Intern
          </Button>
        </Box>
      </div>

      {!allJobs || allJobs.length === 0 ? (
        <div className="p-6">
          {debouncedSearchQuery || jobTypeFilters.length > 0
            ? "No jobs found matching your search criteria"
            : "No Job For now"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4" style={{ marginLeft: '40px', marginRight: '40px' }}>
            {allJobs.map((job, index) => {
              if (allJobs.length === index + 1) {
                return (
                  <div ref={lastJobElementRef} key={job.id}>
                    <JobCard job={job} />
                  </div>
                );
              } else {
                return (
                  <div key={job.id}>
                    <JobCard job={job} />
                  </div>
                );
              }
            })}
          </div>

          {loading && (
            <div className="mt-4 text-center py-4">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
              <p className="mt-2 text-gray-600">Loading more jobs...</p>
            </div>
          )}

          {!hasMore && allJobs.length > 0 && (
            <div className="mt-6 text-center py-4 text-gray-500 border-t">
              No more jobs to load
            </div>
          )}
        </>
      )}
    </Box>
  );
}

export default JobAllRequest;